import { ethers } from 'ethers';

async function getTxnData({ provider, mnemonic, chainId }) {
  try {
    const wallet = ethers.Wallet.fromMnemonic(mnemonic);

    if (!mnemonic || !wallet?.address || !chainId) {
      const newError = new Error('Failed to send transaction');
      newError.ErrorCode = 60001;
      throw newError;
    }

    const connectedWallet = wallet.connect(provider);
    const latestNonce = await provider.getTransactionCount(wallet.address, 'latest');
    const feeData = await provider.getFeeData();

    // Safe fallback values for gas fees
    const maxFeePerGas = feeData.maxFeePerGas ?? ethers.utils.parseUnits('2', 'gwei');
    const maxPriorityFeePerGas =
      feeData.maxPriorityFeePerGas ?? ethers.utils.parseUnits('1', 'gwei');
    return {
      connectedWallet,
      latestNonce,
      maxFeePerGas,
      maxPriorityFeePerGas,
    };
  } catch (err) {
    if (err.ErrorCode) {
      throw err;
    }
    const error = new Error('Failed to generate transaction');
    throw error;
  }
}

export async function sendETHTransaction({
  to,
  value,
  provider,
  mnemonic,
  chainId,
  isMaxSend,
}) {
  try {
    const { connectedWallet, latestNonce, maxFeePerGas, maxPriorityFeePerGas } =
      await getTxnData({ to, value, provider, mnemonic, chainId });

    const amount = ethers.utils.parseEther(value).toString();
    const balance = await provider.getBalance(connectedWallet.address);
    let estimatedGasLimit;

    try {
      estimatedGasLimit = await provider.estimateGas({
        to,
        value: amount,
      });
    } catch (err) {
      const error = new Error('Failed to estimate gas limit');
      error.ErrorCode = 60003;
      throw error;
    }

    const amountToSend = isMaxSend
      ? balance.sub(estimatedGasLimit.mul(maxFeePerGas).mul(120).div(100)).toString()
      : amount;

    const tx = {
      to,
      value: amountToSend,
      nonce: latestNonce,
      gasLimit: estimatedGasLimit,
      maxFeePerGas,
      maxPriorityFeePerGas,
      chainId: chainId,
      type: 2,
    };

    const signedTx = await connectedWallet.signTransaction(tx);
    const txResponse = await provider.sendTransaction(signedTx);

    return txResponse;
  } catch (err) {
    if (err.ErrorCode) {
      throw err;
    }
    const error = new Error('Failed to send transaction');
    throw error;
  }
}

export async function sendBTCTransaction({
  to,
  value,
  mnemonic,
  network,
  from,
  isMaxSend,
}) {
  try {
    const networkType = network?.name === 'sepolia' ? 'test3' : 'main';

    // Call the API endpoint to handle the transaction
    const response = await fetch('/api/coins/btcSendTxn', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        toAddress: to,
        amount: value,
        mnemonic: mnemonic,
        network: networkType,
        sourceAddress: from,
        isMaxSend,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      const newError = new Error(data.error || 'Failed to send BTC transaction');
      newError.ErrorCode = 60005;
      throw newError;
    }

    return {
      success: true,
      hash: data.hash,
      network: data.network,
    };
  } catch (err) {
    if (err.ErrorCode) {
      throw err;
    }
    const error = new Error(`Failed to send BTC transaction: ${err.message}`);
    error.ErrorCode = 60005;
    throw error;
  }
}

export async function sendERCTransaction({
  to,
  value,
  provider,
  mnemonic,
  chainId,
  tokenAddress,
  decimals,
  isMaxSend,
}) {
  try {
    const { connectedWallet, latestNonce, maxFeePerGas, maxPriorityFeePerGas } =
      await getTxnData({ to, value, provider, mnemonic, chainId, isERC: true });

    let balance;
    if (tokenAddress) {
      const erc20Interface = new ethers.utils.Interface([
        'function balanceOf(address owner) view returns (uint256)',
      ]);
      const tokenContract = new ethers.Contract(tokenAddress, erc20Interface, provider);
      balance = await tokenContract.balanceOf(connectedWallet.address);
    } else {
      balance = await provider.getBalance(connectedWallet.address);
    }
    const amount = isMaxSend
      ? balance.toString()
      : ethers.utils.parseUnits(value.toString(), decimals).toString();

    const erc20Interface = new ethers.utils.Interface([
      'function transfer(address to, uint256 amount) returns (bool)',
    ]);
    const contract = new ethers.Contract(tokenAddress, erc20Interface, connectedWallet);

    let estimatedGasLimit;
    try {
      estimatedGasLimit = await contract.estimateGas.transfer(to, amount);
      estimatedGasLimit = estimatedGasLimit.mul(120).div(100); // 20% buffer
      if (estimatedGasLimit.lt(ethers.BigNumber.from(70000))) {
        estimatedGasLimit = ethers.BigNumber.from(70000); // Force minimum gas limit
      }
    } catch (err) {
      const error = new Error('Failed to estimate gas limit for ERC20 transfer');
      error.ErrorCode = 60004;
      throw error;
    }

    const tx = await contract.transfer(to, amount, {
      nonce: latestNonce,
      gasLimit: estimatedGasLimit,
      maxFeePerGas,
      maxPriorityFeePerGas,
      type: 2,
    });

    const receipt = await tx.wait();

    return {
      hash: receipt.transactionHash,
      success: receipt.status === 1,
      blockNumber: receipt.blockNumber,
    };
  } catch (err) {
    if (err.ErrorCode) {
      throw err;
    }
    const error = new Error(`Failed to send ERC20 transaction: ${err.message}`);
    error.ErrorCode = 60005;
    throw error;
  }
}

export async function sendL2Transaction({ to, value, mnemonic, l2ChainInfo, isMaxSend }) {
  try {
    const l2Provider = new ethers.providers.JsonRpcProvider(l2ChainInfo.rpcNodeUrl, {
      name: l2ChainInfo.chainName,
      chainId: Number(l2ChainInfo.chainId),
    });

    const wallet = ethers.Wallet.fromMnemonic(mnemonic);
    const connectedWallet = wallet.connect(l2Provider);
    const latestNonce = await l2Provider.getTransactionCount(wallet.address, 'latest');
    const feeData = await l2Provider.getFeeData();
    const gasPrice = feeData.gasPrice || ethers.utils.parseUnits('10', 'gwei'); // Fallback if null
    const amount = ethers.utils.parseUnits(value, l2ChainInfo.decimalPrecision);
    const balance = await l2Provider.getBalance(wallet.address);

    let estimatedGasLimit;
    try {
      estimatedGasLimit = await l2Provider.estimateGas({
        to,
        value: amount,
        from: wallet.address,
      });

      // Add 20% buffer to gas limit
      estimatedGasLimit = estimatedGasLimit.mul(120).div(100); // Add 20% buffer
    } catch (err) {
      const error = new Error('Failed to estimate gas limit on L2 network');
      error.ErrorCode = 60006;
      throw error;
    }
    const amountToSend = isMaxSend
      ? balance.sub(estimatedGasLimit.mul(gasPrice).mul(120).div(100))
      : amount;
    const tx = {
      to,
      value: amountToSend,
      nonce: latestNonce,
      gasLimit: estimatedGasLimit,
      gasPrice,
      chainId: Number(l2ChainInfo.chainId),
      type: 0, // Force legacy transaction type
    };
    const txResponse = await connectedWallet.sendTransaction(tx);

    const receipt = await txResponse.wait(1);

    return {
      hash: receipt.transactionHash,
      success: receipt.status === 1,
      network: l2ChainInfo.chainName,
    };
  } catch (err) {
    if (err.ErrorCode) {
      throw err;
    }
    const error = new Error(`Failed to send L2 transaction: ${err.message}`);
    error.ErrorCode = 60006;
    throw error;
  }
}

export async function sendBep20Transaction({
  to,
  value,
  mnemonic,
  bep20Info,
  isMaxSend,
  bep20Provider,
}) {
  const ERC20_ABI = [
    'function transfer(address to, uint256 value) public returns (bool)',
    'function balanceOf(address owner) view returns (uint256)',
  ];
  const tokenAddress = bep20Info.definitionAddress;
  const decimals = bep20Info.decimalPrecision;
  try {
    const wallet = ethers.Wallet.fromMnemonic(mnemonic).connect(bep20Provider);
    const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, wallet);
    let amountToSend;
    if (isMaxSend) {
      // Get current balance for max send
      const balance = await tokenContract.balanceOf(wallet.address);
      amountToSend = balance;
    } else {
      amountToSend = ethers.utils.parseUnits(value.toString(), decimals);
    }

    const tx = await tokenContract.transfer(to, amountToSend);

    const receipt = await tx.wait();

    return {
      hash: receipt.transactionHash,
      success: receipt.status === 1,
      network: bep20Info.blockchainConfiguration,
    };
  } catch (err) {
    if (err.ErrorCode) {
      throw err;
    }
    const error = new Error(`Failed to send BEP20 transaction: ${err.message}`);
    error.ErrorCode = 60006;
    throw error;
  }
}

export async function getL2Fees({ amount, to, walletAddress, l2ChainInfo }) {
  const l2Provider = new ethers.providers.JsonRpcProvider(l2ChainInfo.rpcNodeUrl, {
    name: l2ChainInfo.chainName,
    chainId: Number(l2ChainInfo.chainId),
  });
  const latestNonce = await l2Provider.getTransactionCount(walletAddress, 'latest');
  const gasPrice = await l2Provider.getGasPrice();
  const amountToSend = ethers.utils.parseUnits(amount, l2ChainInfo.decimalPrecision);

  let estimatedGasLimit;
  try {
    estimatedGasLimit = await l2Provider.estimateGas({
      to,
      value: amountToSend,
      from: walletAddress,
      nonce: latestNonce,
      gasPrice,
    });

    const baseFee = estimatedGasLimit.mul(gasPrice);
    const fee = baseFee.mul(120).div(100); // Add 20% buffer
    const formattedFee = ethers.utils.formatUnits(fee, l2ChainInfo.decimalPrecision);
    return { coin: formattedFee, gasFeeLoading: false };
  } catch (err) {
    if (
      err?.code === 'UNPREDICTABLE_GAS_LIMIT' ||
      (err?.error?.body && err?.error?.body.includes('gas required exceeds allowance'))
    ) {
      const error = new Error('Insufficient funds');
      error.ErrorCode = 60004;
      throw error;
    }
    const error = new Error('Failed to estimate fees on L2 network');
    error.ErrorCode = 60006;
    throw error;
  }
}

export async function sendBinanceTransaction({
  to,
  value,
  mnemonic,
  isMaxSend,
  binanceInfo,
  bep20Provider,
}) {
  try {
    const wallet = ethers.Wallet.fromMnemonic(mnemonic).connect(bep20Provider);

    const latestNonce = await bep20Provider.getTransactionCount(wallet.address, 'latest');
    const gasPrice = await bep20Provider.getGasPrice();
    const balance = await bep20Provider.getBalance(wallet.address);

    // For non-max sends, parse the value to Wei
    let amountToSend = isMaxSend ? balance : ethers.utils.parseEther(value.toString());

    // Estimate gas limit for the transaction
    let estimatedGasLimit;
    try {
      estimatedGasLimit = await bep20Provider.estimateGas({
        to,
        value: amountToSend,
        from: wallet.address,
      });
      // Add 20% buffer to gas limit
      estimatedGasLimit = estimatedGasLimit.mul(120).div(100);
    } catch (err) {
      const error = new Error('Failed to estimate gas limit for BNB transfer');
      error.ErrorCode = 60004;
      throw error;
    }

    // Calculate total gas cost with 20% buffer
    const gasCost = estimatedGasLimit.mul(gasPrice).mul(120).div(100);

    // If max send, subtract gas cost from balance
    if (isMaxSend) {
      amountToSend = balance.sub(gasCost);
      if (amountToSend.lte(0)) {
        const error = new Error('Insufficient funds to cover gas');
        error.ErrorCode = 60004;
        throw error;
      }
    }
    const tx = {
      to,
      value: amountToSend,
      nonce: latestNonce,
      gasLimit: estimatedGasLimit,
      gasPrice,
      chainId: Number(binanceInfo.blockchainConfiguration.chainId),
    };

    const txResponse = await wallet.sendTransaction(tx);
    const receipt = await txResponse.wait();

    return {
      hash: receipt.transactionHash,
      success: receipt.status === 1,
      network: binanceInfo.blockchainConfiguration.name,
    };
  } catch (err) {
    if (err.ErrorCode) {
      throw err;
    }
    const error = new Error(`Failed to send BNB transaction: ${err.message}`);
    error.ErrorCode = 60006;
    throw error;
  }
}

export async function sendLegacyETHTransaction({
  to,
  value,
  provider,
  privateKey,
  chainId,
  isMaxSend,
}) {
  try {
    const wallet = new ethers.Wallet(privateKey, provider);
    const latestNonce = await provider.getTransactionCount(wallet.address, 'latest');
    const feeData = await provider.getFeeData();

    // Safe fallback values for gas fees
    const maxFeePerGas = feeData.maxFeePerGas ?? ethers.utils.parseUnits('2', 'gwei');
    const maxPriorityFeePerGas =
      feeData.maxPriorityFeePerGas ?? ethers.utils.parseUnits('1', 'gwei');

    const amount = ethers.utils.parseEther(value).toString();
    const balance = await provider.getBalance(wallet.address);
    let estimatedGasLimit;

    try {
      estimatedGasLimit = await provider.estimateGas({
        to,
        value: amount,
      });
    } catch (err) {
      const error = new Error('Failed to estimate gas limit');
      error.ErrorCode = 60003;
      throw error;
    }

    const amountToSend = isMaxSend
      ? balance.sub(estimatedGasLimit.mul(maxFeePerGas).mul(120).div(100)).toString()
      : amount;

    const tx = {
      to,
      value: amountToSend,
      nonce: latestNonce,
      gasLimit: estimatedGasLimit,
      maxFeePerGas,
      maxPriorityFeePerGas,
      chainId: chainId,
      type: 2,
    };

    const signedTx = await wallet.signTransaction(tx);
    const txResponse = await provider.sendTransaction(signedTx);

    return txResponse;
  } catch (err) {
    if (err.ErrorCode) {
      throw err;
    }
    const error = new Error('Failed to send legacy transaction');
    throw error;
  }
}

export async function sendLegacyERCTransaction({
  to,
  value,
  provider,
  privateKey,
  tokenAddress,
  decimals,
  isMaxSend,
}) {
  try {
    const wallet = new ethers.Wallet(privateKey, provider);

    const latestNonce = await provider.getTransactionCount(wallet.address, 'latest');

    const feeData = await provider.getFeeData();

    const maxFeePerGas = feeData.maxFeePerGas ?? ethers.utils.parseUnits('2', 'gwei');
    const maxPriorityFeePerGas =
      feeData.maxPriorityFeePerGas ?? ethers.utils.parseUnits('1', 'gwei');

    let balance;
    if (tokenAddress) {
      const erc20Interface = new ethers.utils.Interface([
        'function balanceOf(address owner) view returns (uint256)',
      ]);
      const tokenContract = new ethers.Contract(tokenAddress, erc20Interface, provider);
      balance = await tokenContract.balanceOf(wallet.address);
    } else {
      balance = await provider.getBalance(wallet.address);
    }

    const amount = isMaxSend
      ? balance.toString()
      : ethers.utils.parseUnits(value.toString(), decimals).toString();

    const erc20Interface = new ethers.utils.Interface([
      'function transfer(address to, uint256 amount) returns (bool)',
    ]);
    const contract = new ethers.Contract(tokenAddress, erc20Interface, wallet);

    let estimatedGasLimit;
    try {
      estimatedGasLimit = await contract.estimateGas.transfer(to, amount);
      estimatedGasLimit = estimatedGasLimit.mul(120).div(100); // 20% buffer
      if (estimatedGasLimit.lt(ethers.BigNumber.from(70000))) {
        estimatedGasLimit = ethers.BigNumber.from(70000); // Force minimum gas limit
      }
    } catch (err) {
      console.error('sendLegacyERCTransaction - Gas estimation failed:', err);
      const error = new Error('Failed to estimate gas limit for ERC20 transfer');
      error.ErrorCode = 60004;
      throw error;
    }

    const tx = await contract.transfer(to, amount, {
      nonce: latestNonce,
      gasLimit: estimatedGasLimit,
      maxFeePerGas,
      maxPriorityFeePerGas,
      type: 2,
    });

    const receipt = await tx.wait();

    return {
      hash: receipt.transactionHash,
      success: receipt.status === 1,
      blockNumber: receipt.blockNumber,
    };
  } catch (err) {
    console.error('sendLegacyERCTransaction - Error:', err);
    if (err.ErrorCode) {
      throw err;
    }
    const error = new Error(`Failed to send legacy ERC20 transaction: ${err.message}`);
    error.ErrorCode = 60005;
    throw error;
  }
}

export async function sendLegacyBep20Transaction({
  to,
  value,
  privateKey,
  bep20Info,
  isMaxSend,
  bep20Provider,
}) {
  const ERC20_ABI = [
    'function transfer(address to, uint256 value) public returns (bool)',
    'function balanceOf(address owner) view returns (uint256)',
  ];
  const tokenAddress = bep20Info.definitionAddress;
  const decimals = bep20Info.decimalPrecision;

  try {
    const wallet = new ethers.Wallet(privateKey).connect(bep20Provider);
    const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, wallet);

    let amountToSend;
    if (isMaxSend) {
      // Get current balance for max send
      const balance = await tokenContract.balanceOf(wallet.address);
      amountToSend = balance;
    } else {
      amountToSend = ethers.utils.parseUnits(value.toString(), decimals);
    }

    const tx = await tokenContract.transfer(to, amountToSend);
    const receipt = await tx.wait();

    return {
      hash: receipt.transactionHash,
      success: receipt.status === 1,
      network: bep20Info.blockchainConfiguration,
    };
  } catch (err) {
    if (err.ErrorCode) {
      throw err;
    }
    const error = new Error(`Failed to send legacy BEP20 transaction: ${err.message}`);
    error.ErrorCode = 60006;
    throw error;
  }
}

export async function sendLegacyBinanceTransaction({
  to,
  value,
  privateKey,
  isMaxSend,
  binanceInfo,
  bep20Provider,
}) {
  try {
    const wallet = new ethers.Wallet(privateKey).connect(bep20Provider);

    const latestNonce = await bep20Provider.getTransactionCount(wallet.address, 'latest');
    const gasPrice = await bep20Provider.getGasPrice();
    const balance = await bep20Provider.getBalance(wallet.address);

    // For non-max sends, parse the value to Wei
    let amountToSend = isMaxSend ? balance : ethers.utils.parseEther(value.toString());

    // Estimate gas limit for the transaction
    let estimatedGasLimit;
    try {
      estimatedGasLimit = await bep20Provider.estimateGas({
        to,
        value: amountToSend,
        from: wallet.address,
      });
      // Add 20% buffer to gas limit
      estimatedGasLimit = estimatedGasLimit.mul(120).div(100);
    } catch (err) {
      const error = new Error('Failed to estimate gas limit for BNB transfer');
      error.ErrorCode = 60004;
      throw error;
    }

    // Calculate total gas cost with 20% buffer
    const gasCost = estimatedGasLimit.mul(gasPrice).mul(120).div(100);

    // If max send, subtract gas cost from balance
    if (isMaxSend) {
      amountToSend = balance.sub(gasCost);
      if (amountToSend.lte(0)) {
        const error = new Error('Insufficient funds to cover gas');
        error.ErrorCode = 60004;
        throw error;
      }
    }
    const tx = {
      to,
      value: amountToSend,
      nonce: latestNonce,
      gasLimit: estimatedGasLimit,
      gasPrice,
      chainId: Number(binanceInfo.blockchainConfiguration.chainId),
    };

    const txResponse = await wallet.sendTransaction(tx);
    const receipt = await txResponse.wait();

    return {
      hash: receipt.transactionHash,
      success: receipt.status === 1,
      network: binanceInfo.blockchainConfiguration.name,
    };
  } catch (err) {
    if (err.ErrorCode) {
      throw err;
    }
    const error = new Error(`Failed to send legacy BNB transaction: ${err.message}`);
    error.ErrorCode = 60006;
    throw error;
  }
}

export async function sendLegacyBTCTransaction({
  to,
  value,
  privateKey,
  network,
  from,
  isMaxSend,
}) {
  try {
    const networkType = network?.name === 'sepolia' ? 'test3' : 'main';

    // For BTC transactions, we need to use the private key directly
    // The API endpoint will need to be modified to accept privateKey instead of mnemonic
    const response = await fetch('/api/coins/btcSendTxn', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        toAddress: to,
        amount: value,
        privateKey: privateKey, // Send private key instead of mnemonic
        network: networkType,
        sourceAddress: from,
        isMaxSend,
        isLegacy: true, // Flag to indicate this is a legacy transaction
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      const newError = new Error(data.error || 'Failed to send legacy BTC transaction');
      newError.ErrorCode = 60005;
      throw newError;
    }

    return {
      success: true,
      hash: data.hash,
      network: data.network,
    };
  } catch (err) {
    if (err.ErrorCode) {
      throw err;
    }
    const error = new Error(`Failed to send legacy BTC transaction: ${err.message}`);
    error.ErrorCode = 60005;
    throw error;
  }
}

export async function sendLegacyL2Transaction({
  to,
  value,
  privateKey,
  l2ChainInfo,
  isMaxSend,
}) {
  try {
    const l2Provider = new ethers.providers.JsonRpcProvider(l2ChainInfo.rpcNodeUrl, {
      name: l2ChainInfo.chainName,
      chainId: Number(l2ChainInfo.chainId),
    });

    const wallet = new ethers.Wallet(privateKey);
    const connectedWallet = wallet.connect(l2Provider);
    const latestNonce = await l2Provider.getTransactionCount(wallet.address, 'latest');
    const feeData = await l2Provider.getFeeData();
    const gasPrice = feeData.gasPrice || ethers.utils.parseUnits('10', 'gwei'); // Fallback if null
    const amount = ethers.utils.parseUnits(value, l2ChainInfo.decimalPrecision);
    const balance = await l2Provider.getBalance(wallet.address);

    let estimatedGasLimit;
    try {
      estimatedGasLimit = await l2Provider.estimateGas({
        to,
        value: amount,
        from: wallet.address,
      });

      // Add 20% buffer to gas limit
      estimatedGasLimit = estimatedGasLimit.mul(120).div(100); // Add 20% buffer
    } catch (err) {
      const error = new Error('Failed to estimate gas limit on L2 network');
      error.ErrorCode = 60006;
      throw error;
    }
    const amountToSend = isMaxSend
      ? balance.sub(estimatedGasLimit.mul(gasPrice).mul(120).div(100))
      : amount;
    const tx = {
      to,
      value: amountToSend,
      nonce: latestNonce,
      gasLimit: estimatedGasLimit,
      gasPrice,
      chainId: Number(l2ChainInfo.chainId),
      type: 0, // Force legacy transaction type
    };
    const txResponse = await connectedWallet.sendTransaction(tx);

    const receipt = await txResponse.wait(1);

    return {
      hash: receipt.transactionHash,
      success: receipt.status === 1,
      network: l2ChainInfo.chainName,
    };
  } catch (err) {
    if (err.ErrorCode) {
      throw err;
    }
    const error = new Error(`Failed to send legacy L2 transaction: ${err.message}`);
    error.ErrorCode = 60006;
    throw error;
  }
}
